import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../styles/landing.css';

const Cookies = () => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const nav = document.querySelector('.landing-nav');
        const handleScroll = () => {
            if (window.scrollY > 50) {
                nav?.classList.add('scrolled');
            } else {
                nav?.classList.remove('scrolled');
            }
        };
        window.addEventListener('scroll', handleScroll);

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in-view');
                    obs.unobserve(entry.target);
                }
            });
        }, observerOptions);

        document.querySelectorAll('.scroll-animate').forEach((element) => {
            observer.observe(element);
        });

        window.scrollTo(0, 0);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    return (
        <div className="landing-body" style={{ background: '#fff' }}>
            <style>
                {`
                    .legal-page {
                        background: #fff;
                        color: #111827;
                        padding: 120px 20px 80px;
                        line-height: 1.6;
                    }

                    .legal-container {
                        max-width: 800px;
                        margin: 0 auto;
                    }

                    .legal-container h1 {
                        font-size: 2.5rem;
                        font-weight: 800;
                        margin-bottom: 40px;
                        color: #2A3F54;
                    }

                    .legal-container h2 {
                        font-size: 1.5rem;
                        font-weight: 700;
                        margin: 40px 0 20px;
                        color: #2A3F54;
                    }

                    .legal-container p {
                        margin-bottom: 20px;
                        color: #4b5563;
                    }

                    .legal-container ul {
                        margin-bottom: 20px;
                        padding-left: 20px;
                        color: #4b5563;
                    }

                    .legal-container li {
                        margin-bottom: 10px;
                    }

                    .last-updated {
                        color: #9ca3af;
                        font-size: 0.9rem;
                        margin-bottom: 40px;
                    }
                `}
            </style>

            {/* Navigation */}
            <nav className={`landing-nav scrolled${menuOpen ? ' nav-open' : ''}`}>
                <div className="logo">
                    <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fa-solid fa-layer-group logo-icon" style={{ color: 'var(--lp-primary-orange)' }}></i>
                        <span style={{ color: 'var(--lp-primary-orange)' }}>envion</span>
                    </Link>
                </div>
                <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
                    <i className={menuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'}></i>
                </button>
                <div className="nav-links">
                    <Link to="/solutions" className="nav-link">Solutions</Link>
                    <Link to="/about" className="nav-link">About Us</Link>
                    <Link to="/#how-it-works" className="nav-link">How it Works</Link>
                    <Link to="/contact" className="nav-link">Contact</Link>
                </div>
                <div className="auth-buttons">
                    <button onClick={() => navigate('/login')} className="btn-lp btn-lp-secondary w-full sm:w-auto text-center" style={{ cursor: 'pointer' }}>Log In</button>
                    <Link to="/contact" className="btn-lp btn-lp-primary">Get a Quote</Link>
                </div>
            </nav>

            <main className="legal-page">
                <div className="legal-container scroll-animate animate-fade-in">
                    <h1>Cookies Policy</h1>
                    <p className="last-updated">Last updated: March 2025</p>

                    <h2>1. Introduction</h2>
                    <p>This Cookies Policy explains what cookies are and how Envion Ltd ("Envion", "we", "our", "us") uses them on our website at envion.org.uk. Please read this policy alongside our Privacy Policy, which explains how we handle personal data more broadly.</p>

                    <h2>2. What Are Cookies?</h2>
                    <p>Cookies are small text files placed on your device when you visit a website. They are widely used to make websites function correctly, improve user experience, and provide information to website owners. Cookies may be set by the website you are visiting ("first-party cookies") or by third-party services operating on that website ("third-party cookies").</p>

                    <h2>3. How We Use Cookies</h2>
                    <p>We use cookies for the following purposes: to ensure our website functions correctly and securely, to understand how visitors use our website so we can improve it, to remember your preferences and provide enhanced functionality, to facilitate secure payment processing, and to enable demo booking functionality.</p>

                    <h2>4. Types of Cookies We Use</h2>
                    <h3>4.1 Essential Cookies</h3>
                    <p>These cookies are necessary for the website to function properly. They enable core functionality such as security, page navigation, and access to secure areas of the website. The website cannot function correctly without these cookies.</p>
                    <p>You may disable essential cookies by changing your browser settings, but doing so may affect how the website works.</p>

                    <h3>4.2 Analytics Cookies</h3>
                    <p>We use Google Analytics to collect anonymised information about how visitors use our website, including which pages are visited most often, how long visitors spend on each page, and how visitors arrived at the website. This helps us understand usage patterns and improve the website.</p>
                    <p>Google Analytics anonymises your IP address before it is transmitted to Google. All data collected is aggregated and cannot be used to identify you personally.</p>
                    <ul>
                        <li><strong>Provider:</strong> Google</li>
                        <li><strong>Purpose:</strong> Website usage analytics</li>
                        <li><strong>Data location:</strong> United States (anonymised data only)</li>
                    </ul>
                    <p>You can opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on, available at <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">tools.google.com/dlpage/gaoptout</a>.</p>

                    <h3>4.3 Functionality Cookies</h3>
                    <p>These cookies allow the website to remember choices you make and provide enhanced, personalised features. This may include remembering your preferences, language settings, or form entries.</p>

                    <h3>4.4 Payment Processing Cookies</h3>
                    <p>We use Stripe to process payments securely. When you make a payment through our website, Stripe may place cookies on your device to facilitate secure transaction processing and to help prevent fraud.</p>
                    <p>Envion does not store your payment card details. All payment data is handled directly by Stripe.</p>
                    <ul>
                        <li><strong>Provider:</strong> Stripe</li>
                        <li><strong>Purpose:</strong> Secure payment processing and fraud prevention</li>
                        <li><strong>More information:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
                    </ul>

                    <h3>4.5 Booking Cookies</h3>
                    <p>We use Microsoft Bookings to manage demo session scheduling. When you book a demo through our website, Microsoft may place cookies to facilitate the booking process and remember your scheduling preferences.</p>
                    <ul>
                        <li><strong>Provider:</strong> Microsoft</li>
                        <li><strong>Purpose:</strong> Demo scheduling and booking management</li>
                        <li><strong>More information:</strong> <a href="https://privacy.microsoft.com" target="_blank" rel="noopener noreferrer">privacy.microsoft.com</a></li>
                    </ul>

                    <h2>5. Cookie Consent</h2>
                    <p>When you first visit our website, you will be presented with a cookie consent banner that allows you to accept or reject non-essential cookies. Essential cookies that are necessary for the website to function will always be active.</p>
                    <p>You can change your cookie preferences at any time by clearing your browser cookies and revisiting the website, at which point the consent banner will appear again.</p>

                    <h2>6. Managing Cookies in Your Browser</h2>
                    <p>Most web browsers allow you to manage cookies through their settings. You can set your browser to block cookies, delete existing cookies, or alert you when a cookie is being set. The following links explain how to manage cookies in common browsers:</p>
                    <ul>
                        <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                        <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                        <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
                        <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471" target="_blank" rel="noopener noreferrer">Safari</a></li>
                    </ul>
                    <p>Please note that blocking or deleting cookies may affect the functionality of our website and prevent certain features from working correctly.</p>

                    <h2>7. Changes to This Policy</h2>
                    <p>We may update this Cookies Policy from time to time to reflect changes in the cookies we use, changes in technology, or changes in legal requirements. When we make material changes, we will update the date at the top of this policy. We encourage you to review this policy periodically.</p>

                    <h2>8. Contact Us</h2>
                    <p>If you have any questions about our use of cookies, please contact us at:</p>
                    <p><strong>Envion Ltd</strong><br />Email: <a href="mailto:enquiries@envion.org.uk">enquiries@envion.org.uk</a></p>
                    <p>For more information about how we handle personal data, please see our <Link to="/privacy">Privacy Policy</Link>.</p>
                </div>
            </main>

            {/* Footer */}
            <footer className="site-footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <h2><i className="fa-solid fa-layer-group"></i> envion</h2>
                        <p className="footer-subtitle"
                            style={{ fontSize: '1.1rem', color: 'white', lineHeight: 1.5, marginBottom: '24px' }}>
                            envion is a modern workforce readiness platform. Enabling faster, smarter onboarding and preboarding
                            for today’s organisations.
                        </p>
                        <div className="footer-contact-info">
                            <p><i className="fa-solid fa-envelope"></i> <a href="mailto:enquiries@envion.org.uk"
                                style={{ color: 'white', textDecoration: 'none' }}>enquiries@envion.org.uk</a></p>
                        </div>
                    </div>

                    <div className="footer-column">
                        <h3>Solutions</h3>
                        <ul className="footer-links">
                            <li><Link to="/solutions#use-cases">Onboarding</Link></li>
                            <li><Link to="/solutions#use-cases">Preboarding</Link></li>
                        </ul>
                    </div>

                    <div className="footer-column">
                        <h3>Company</h3>
                        <ul className="footer-links">
                            <li><Link to="/about">About Us</Link></li>
                            <li><Link to="/contact">Contact</Link></li>
                        </ul>
                    </div>

                    <div className="footer-column">
                        <h3>Connect</h3>
                        <ul className="footer-social-links"
                            style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '16px', marginBottom: '24px' }}>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white' }}><i className="fa-brands fa-instagram"></i></a>
                            </li>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white' }}><i className="fa-brands fa-linkedin"></i></a>
                            </li>
                        </ul>
                        <div className="footer-compliance-badges"
                            style={{ color: 'white', fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.4 }}>
                            <p style={{ marginBottom: '4px' }}>GDPR Compliant</p>
                            <p>Registered with ICO</p>
                        </div>
                    </div>
                </div>

                <div className="footer-bottom">
                    <div className="footer-bottom-grid">
                        <div className="footer-legal-info">
                            <p className="copyright">&copy; 2026 envion Inc. All rights reserved.</p>
                        </div>
                        <div className="footer-legal-links">
                            <Link to="/terms">Terms & Conditions</Link>
                            <Link to="/privacy">Privacy Policy</Link>
                            <Link to="/cookies">Cookies Policy</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Cookies;
